import React from "react";
import PropTypes from "prop-types";
import injectSheet from "react-jss";

import Main from "../components/Main";
import Article from "../components/Main/Article";
import PageHeader from "../components/Page/PageHeader";
import Content from "../components/Main/Content";
import Footer from "../components/Footer/Footer";

import Link from "gatsby-link";

const styles = theme => ({});

const Support = () => {
  return (
    <Main>
      <Article>
        <PageHeader title="Terms of Use / Privacy Policy" />
        <Content>
          <h1>Terms of Use</h1>
          <p class="meta">
            Effective Date: August 21, 2024 &nbsp;·&nbsp; Last Updated: June 5, 2026 &nbsp;·&nbsp;
            Site: <a href="https://www.projectsurfhelmet.com">projectsurfhelmet.com</a>
          </p>

          <div class="highlight-box">
            <strong>The short version:</strong> This is a personal hobby site run by one surfer.
            Everything here is opinion. Nothing is professional safety advice. Your equipment
            decisions are your own. I make a small affiliate commission on some purchases at no cost
            to you. Use this site at your own risk.
          </div>

          <h2>1. About This Site</h2>
          <p>
            Project Surf Helmet is a personal hobby project operated by one individual — not a
            company, LLC, corporation, or any other legal business entity. There is no team, no
            staff, and no commercial organization behind this site. It exists purely because I am a
            surfer who cares about reducing head injuries in the water and wants to share honest
            research with other surfers.
          </p>
          <p>
            By accessing this site, you agree to these terms. If you do not agree, please stop using
            the site.
          </p>

          <h2>2. Everything Here Is Personal Opinion</h2>
          <p>
            Every review, ranking, comparison, rating, recommendation, or statement on this site —
            including any content about helmet materials, foam technologies, safety characteristics,
            or fitness for purpose — is strictly my personal opinion based on my own research,
            reading, and experience. I am not an engineer, a certified safety tester, a materials
            scientist, or a medical professional.
          </p>
          <p>
            Nothing on this site constitutes professional safety advice, engineering analysis,
            medical guidance, regulatory certification, or any form of expert recommendation. No
            content on this site should substitute for professional evaluation or official safety
            certifications.
          </p>

          <h2>3. Assumption of Risk &amp; No Liability</h2>
          <p>
            Surfing involves inherent and serious risks of injury or death regardless of what
            equipment you choose. By using this site, you expressly acknowledge and agree that:
          </p>
          <ul>
            <li>
              Any decision about which helmet to purchase, wear, or recommend to others is yours
              alone.
            </li>
            <li>
              I accept no responsibility whatsoever for any injury, death, property damage,
              financial loss, or other harm of any kind arising from your use or non-use of any
              product discussed or linked to on this site.
            </li>
            <li>All content is consumed entirely at your own risk.</li>
            <li>
              I make no warranties — express or implied — about the accuracy, completeness,
              currency, or reliability of any information on this site.
            </li>
            <li>
              Product specifications, safety ratings, prices, availability, and manufacturer details
              change frequently. Always verify directly with the manufacturer or retailer before
              making any purchasing decision.
            </li>
            <li>
              Helmet performance in any real-world situation can differ significantly from anything
              described in a review.
            </li>
          </ul>
          <p>
            <strong>
              To the maximum extent permitted by applicable law, I expressly disclaim all liability
              for any direct, indirect, incidental, special, consequential, exemplary, or punitive
              damages arising from your use of this site, your reliance on any content herein, or
              any decision made in connection with this site.
            </strong>{" "}
            This disclaimer applies whether the claim is based in contract, tort, negligence, strict
            liability, or any other legal or equitable theory.
          </p>
          <p>
            If any limitation of liability in these terms is found unenforceable by a court, that
            limitation will be enforced to the maximum extent permitted by law, and all other
            limitations remain in full effect.
          </p>

          <h2>4. Affiliate Disclosure</h2>
          <p>
            This site participates in affiliate programs, including the Amazon Associates Program
            and potentially other retailer affiliate programs. This means I may earn a small
            commission if you purchase a product through a link on this site — at no additional cost
            to you.
          </p>
          <p>
            Affiliate relationships <strong>never</strong> influence my reviews or rankings. My only
            goal is to give honest assessments to help surfers make better-informed decisions about
            safety equipment. Products I review negatively often have affiliate links too.
          </p>

          <h2>5. No Accounts or Payments</h2>
          <p>
            This site has no user registration system and does not accept payments of any kind. You
            do not need to provide any personal information to read content on this site.
          </p>

          <h2>6. External Links</h2>
          <p>
            This site contains links to third-party websites including product pages, manufacturer
            sites, and academic research. I have no control over and take no responsibility for the
            content, accuracy, safety, privacy practices, or terms of any linked external site.
            Following an external link is entirely at your own risk.
          </p>

          <h2>7. Intellectual Property</h2>
          <p>
            All original content on this site — including text, original photographs, and graphics
            created by me — is my personal intellectual property. You may quote or reference content
            from this site with appropriate attribution. Wholesale copying, republication, or
            commercial use of site content is not permitted without prior written permission.
          </p>
          <p>
            For copyright or DMCA inquiries, contact:
            <a href="mailto:cole@projectsurfhelmet.com">cole@projectsurfhelmet.com</a>
          </p>

          <h2>8. Children</h2>
          <p>
            This site is not directed at children under 13. I do not knowingly collect personal
            information from children. If you are a parent or guardian and believe your child has
            submitted information through this site, please contact me and I will delete it.
          </p>

          <h2>9. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul>
            <li>
              Scrape, crawl, or systematically harvest content from this site without permission.
            </li>
            <li>Use this site's content for commercial purposes without written permission.</li>
            <li>Attempt to interfere with or disrupt the technical operation of the site.</li>
            <li>
              Use automated tools to access or interact with the site in ways not intended for
              normal browsing.
            </li>
            <li>
              Reproduce or misrepresent site content in a way that could mislead others about its
              source or nature.
            </li>
          </ul>

          <h2>10. "As-Is" Disclaimer</h2>
          <p>
            This site and all its content are provided on an "as-is" and "as-available" basis
            without warranties of any kind. I make no representation that this site will be
            uninterrupted, error-free, or free of harmful code. Use of this site is entirely at your
            own risk.
          </p>

          <h2>11. Governing Law</h2>
          <p>
            This site is operated by an individual residing in California, USA. These terms are
            governed by the laws of the State of California to the extent any legal framework
            applies to a personal hobby website. Nothing in these terms limits any consumer rights
            you may have under the laws of your own jurisdiction.
          </p>

          <h2>12. Changes to These Terms</h2>
          <p>
            I may update these terms at any time. The "Last Updated" date at the top of this page
            will reflect any changes. Continued use of the site after an update constitutes
            acceptance of the revised terms.
          </p>

          <h2>13. Contact</h2>
          <div class="contact-block">
            <strong>Project Surf Helmet</strong>
            <br />
            Personal hobby site — Long Beach, CA, USA<br />
            <a href="mailto:cole@projectsurfhelmet.com">cole@projectsurfhelmet.com</a>
          </div>
        </Content>
        {/*<Form />*/}
      </Article>
      <Footer />
    </Main>
  );
};

Support.propTypes = {
  classes: PropTypes.object.isRequired
};

export default injectSheet(styles)(Support);



// WEBPACK FOOTER //
// ./src/pages/terms.js